import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {PaginateModel} from '../../../shared/models/PaginateModel'
import {SchedulerModel} from '../models/SchedulerModel'
import {ListResponseModel} from '../../../shared/models/ListResponseModel'
import {toast} from 'react-toastify'
import {RootState} from '../../../../setup/redux/Store'
import {SchedulerFilterModel} from '../models/SchedulerListFilterModel'
import {VerifyFolderResult} from '../models/VerifyFolderResult'
import {CreateSchedulerRequestModel} from '../models/CreateSchedulerRequestModel'
import {SchedulerExecuteLogModel} from '../models/SchedulerExecuteLogModel'
import {
  SchedulerOrderListFilterModel,
} from '../models/SchedulerOrderListFilterModel'
import {SchedulerOrderModel} from '../models/SchedulerOrderModel'
import { SchedulerLogFilterModel } from '../models/SchedulerLogListFilterModel'

export interface ISchedulerState {
  list: SchedulerModel[]
  filter: SchedulerFilterModel
  loading: boolean
  transactionLoading: boolean
  currentUserSettingSchedule: SchedulerModel | undefined
  verifyFolderResult: VerifyFolderResult | undefined
  schedulerLogs: SchedulerExecuteLogModel[]
  schedulerLogsFilter: SchedulerLogFilterModel
  schedulerOrders: SchedulerOrderModel[]
  schedulerOrderFilter: SchedulerOrderListFilterModel
}

const initialState: ISchedulerState = {
  list: [],
  filter: {
    page: 1,
    take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
    itemCount: 0,
    pageCount: 1,
  } as PaginateModel,
  loading: false,
  transactionLoading: false,
  currentUserSettingSchedule: undefined,
  verifyFolderResult: undefined,
  schedulerLogs: [],
  schedulerOrders: [],
  schedulerLogsFilter: {
    page: 1,
    take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
    itemCount: 0,
    pageCount: 1,
  } as PaginateModel,
  schedulerOrderFilter: {
    page: 1,
    take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
    itemCount: 0,
    pageCount: 1,
  } as PaginateModel,
}

export const SchedulerSlice = createSlice({
  name: 'Scheduler',
  initialState,
  reducers: {
    getAll: (state) => {
      state.loading = true
      state.filter = {
        page: 1,
        take: Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE),
        itemCount: 0,
        pageCount: 1,
      }
    },
    getAllLogs: (state, action: PayloadAction<{}>) => {
      state.loading = true
    },
    setList: (state, action: PayloadAction<ListResponseModel<SchedulerModel>>) => {
      state.loading = false
      state.list = action.payload.data
      state.filter = {...state.filter, ...action.payload.meta}
    },
    setSchedulerLogList: (
      state,
      action: PayloadAction<ListResponseModel<SchedulerExecuteLogModel>>
    ) => {
      state.loading = false
      state.schedulerLogs = action.payload.data
      state.schedulerLogsFilter = {...state.schedulerLogsFilter, ...action.payload.meta}
    },
    filterSchedulerDataChange: (state, action: PayloadAction<SchedulerLogFilterModel>) => {
      state.loading = true
      state.schedulerLogsFilter = action.payload
    },
    filterDataChange: (state, action: PayloadAction<SchedulerFilterModel>) => {
      state.loading = true
      state.filter = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.transactionLoading = false
      toast.error(action.payload)
    },
    setSuccess: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.transactionLoading = false
      toast.success(action.payload)
    },
    verifyFolder: (state, action: PayloadAction<string>) => {
      state.loading = true
    },
    setVerifyFolderResult: (state, action: PayloadAction<VerifyFolderResult | undefined>) => {
      state.loading = false
      state.verifyFolderResult = action.payload
    },
    create: (state, action: PayloadAction<CreateSchedulerRequestModel>) => {
      state.transactionLoading = true
    },
    update: (state, action: PayloadAction<SchedulerModel>) => {
      state.transactionLoading = true
    },
    trySchedulerSetting: (
      state,
      action: PayloadAction<{folderName: string; schedulerId: string}>
    ) => {
      state.transactionLoading = true
    },
    updateSchedulerSettingStatus: (state, action: PayloadAction<{id: number; status: string}>) => {
      state.transactionLoading = true
    },
    deleteSchedulerSetting: (state, action: PayloadAction<number>) => {
      state.transactionLoading = true
    },
    getSchedulerOrderList: (state, action: PayloadAction<{schedulerId: string}>) => {
      state.loading = true
    },
    setSchedulerOrderList: (
      state,
      action: PayloadAction<ListResponseModel<SchedulerOrderModel>>
    ) => {
      state.loading = false
      state.schedulerOrders = action.payload.data
      state.schedulerOrderFilter = {...state.schedulerOrderFilter, ...action.payload.meta}
    },
    filterSchedulerOrderDataChange: (
      state,
      action: PayloadAction<{schedulerId: string; filter: SchedulerOrderListFilterModel}>
    ) => {
      state.loading = true
      state.schedulerOrderFilter = action.payload.filter
    },
  },
})
export const {
  create,
  update,
  setSuccess,
  getAll,
  setList,
  filterDataChange,
  setError,
  verifyFolder,
  setVerifyFolderResult,
  getAllLogs,
  setSchedulerLogList,
  filterSchedulerDataChange,
  trySchedulerSetting,
  updateSchedulerSettingStatus,
  deleteSchedulerSetting,
  getSchedulerOrderList,
  setSchedulerOrderList,
  filterSchedulerOrderDataChange,
} = SchedulerSlice.actions
export const selectSchedulerLoading = (state: RootState) => state.scheduler.loading
export const selectSchedulerList = (state: RootState) => state.scheduler.list
export const selectSchedulerFilter = (state: RootState) => state.scheduler.filter
export const selectCurrentUserSetting = (state: RootState) =>
  state.scheduler.currentUserSettingSchedule
export const selectVerifyFolderResult = (state: RootState) => state.scheduler.verifyFolderResult
export const selectTransactionLoading = (state: RootState) => state.scheduler.transactionLoading
export const selectSchedulerLogs = (state: RootState) => state.scheduler.schedulerLogs
export const selectSchedulerLogsFilter = (state: RootState) => state.scheduler.schedulerLogsFilter
export const selectSchedulerOrders = (state: RootState) => state.scheduler.schedulerOrders
export const selectSchedulerOrderFilter = (state: RootState) => state.scheduler.schedulerOrderFilter
export default SchedulerSlice.reducer
